import * as React from "react";
import {
  ClientContactSummary,
  ClientsService,
  CognitoUser,
  CognitoUserPoolType,
  CognitoUserSummary,
  ExecutionJob,
  ExecutionJobCollection,
  ExecutionJobsService,
  ExecutionJobStatus,
  ExecutionJobType,
  UserAccountsService,
  UserGroup
} from "gen/clients";
import { DataTable } from "components/DataTable/DataTable";
import { useMutation, useQueries, useQuery, useQueryClient } from "react-query";
import { Form, Formik } from "formik";
import { IconButton, InputBase, Paper, Skeleton, TablePagination, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { DataTableRowAction } from "components/DataTable/types";
import { useClientReadOnly } from "contexts/ClientLockContext";
import { useDialogState } from "hooks/useDialogState";
import { ClientContactDetailsPanel } from "./ClientContactDetailsPanel/ClientContactDetailsPanel";
import Stack from "@mui/material/Stack";
import { formatEpoch } from "utils/dateUtils";
import { InfoIcon } from "../../../../../components/InfoIcon/InfoIcon";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useUserSession } from "hooks/useUserSession";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { useSnackbarAlert } from "hooks/useSnackbarAlert";

interface Props {
  clientId: string;
}

const sortByUsername = (a: { username: string }, b: { username: string }) =>
  a.username.toLowerCase().localeCompare(b.username.toLowerCase());

const BATCH_DISABLE_JOBS_QUERY_KEY = "listBatchDisableAccountJobs";

const ClientContactsTab: React.FC<Props> = ({ clientId }) => {
  const isReadOnly = useClientReadOnly();
  const queryClient = useQueryClient();
  const { roles } = useUserSession();
  const [clientContactsFilter, setClientContactsFilter] = React.useState("");
  const [applyClientContactsFilter, setApplyClientContactsFilter] = React.useState("");
  const [selectedContact, setSelectedContact] = React.useState<ClientContactSummary>();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [isClientContactDetailsPanelOpen, openClientContactDetailsPanel, closeClientContactDetailsPanel] =
    useDialogState();
  const [confirmDialogOpen, openConfirmDialog, closeConfirmDialog] = useDialogState();
  const { snackbarAlert, openSnackbarAlert } = useSnackbarAlert({
    autoHideDuration: 5000,
    vertical: "bottom",
    horizontal: "left"
  });

  const isAdminOrSuperUser = React.useMemo(
    () => roles?.includes(UserGroup.ADMIN) || roles?.includes(UserGroup.SUPER_USER) || false,
    [roles]
  );

  // Track previous job status to detect completion
  const prevJobStatusRef = React.useRef<string | undefined>();

  // Poll for batch disable account execution jobs scoped to this client
  const { data: batchDisableJobs } = useQuery<ExecutionJobCollection>([BATCH_DISABLE_JOBS_QUERY_KEY, clientId], {
    queryFn: () =>
      ExecutionJobsService.listExecutionJobs({
        type: ExecutionJobType.BATCH_CHANGE_CLIENT_CONTACT_ACCOUNT_STATE,
        resourceType: "CLIENT",
        resourceId: clientId,
        pageSize: 1
      }),
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchInterval: data => {
      const lastJob = data?.items?.[0];
      if (lastJob && (lastJob.status === ExecutionJobStatus.PENDING || lastJob.status === ExecutionJobStatus.RUNNING)) {
        return 5000;
      }
      return false;
    }
  });

  const lastBatchDisableJob: ExecutionJob | undefined = batchDisableJobs?.items?.[0];
  const isBatchDisableInProgress =
    lastBatchDisableJob?.status === ExecutionJobStatus.PENDING ||
    lastBatchDisableJob?.status === ExecutionJobStatus.RUNNING;

  // Detect job completion and refresh cognito data
  React.useEffect(() => {
    const currentStatus = lastBatchDisableJob?.status;
    const prevStatus = prevJobStatusRef.current;
    prevJobStatusRef.current = currentStatus;

    if (
      prevStatus &&
      (prevStatus === ExecutionJobStatus.PENDING || prevStatus === ExecutionJobStatus.RUNNING) &&
      currentStatus === ExecutionJobStatus.COMPLETED
    ) {
      // Invalidate all cognito queries to refresh the table
      queryClient.invalidateQueries(["listCognitoUsers"]);
      queryClient.invalidateQueries(["getCognitoUser"]);
      openSnackbarAlert({ message: "Cognito accounts have been disabled successfully.", severity: "success" });
    } else if (
      prevStatus &&
      (prevStatus === ExecutionJobStatus.PENDING || prevStatus === ExecutionJobStatus.RUNNING) &&
      currentStatus === ExecutionJobStatus.FAILED
    ) {
      openSnackbarAlert({ message: "Failed to disable Cognito accounts.", severity: "error" });
    }
  }, [lastBatchDisableJob?.status, queryClient, openSnackbarAlert]);

  const {
    mutate: triggerBatchDisable,
    isLoading: isBatchDisableMutationLoading,
    error: batchDisableError,
    reset: resetBatchDisableError
  } = useMutation({
    mutationFn: ClientsService.batchChangeClientContactAccountState,
    onSuccess: () => {
      closeConfirmDialog();
      queryClient.invalidateQueries([BATCH_DISABLE_JOBS_QUERY_KEY, clientId]);
    }
  });

  const onConfirmBatchDisable = React.useCallback(() => {
    triggerBatchDisable({
      clientId,
      requestBody: {
        enabled: false,
        criteria: {
          excludeFederatedSSO: true
        }
      }
    });
  }, [triggerBatchDisable, clientId]);

  const onCloseConfirmDialog = React.useCallback(() => {
    if (!isBatchDisableMutationLoading) {
      closeConfirmDialog();
      resetBatchDisableError();
    }
  }, [isBatchDisableMutationLoading, closeConfirmDialog, resetBatchDisableError]);

  const {
    data: clientContacts,
    error,
    isLoading
  } = useQuery(["listClientContacts", clientId], {
    queryFn: () => ClientsService.listClientContacts({ clientId }),
    onSuccess: () => window.console.log("listClientContacts query succeeded")
  });

  const filteredClientContacts = React.useMemo(() => {
    const filterTokens = applyClientContactsFilter.toLowerCase().split(" ");
    return (
      clientContacts?.items.filter(contact => {
        for (let i = 0; i < filterTokens.length; i++) {
          const filterToken = filterTokens[i];
          if (
            !(contact.firstName || "").toLowerCase().includes(filterToken) &&
            !(contact.lastName || "").toLowerCase().includes(filterToken) &&
            !contact.email.toLowerCase().includes(filterToken)
          ) {
            return false;
          }
        }
        return true;
      }) || []
    );
  }, [clientContacts?.items, applyClientContactsFilter]);

  const sortedClientContacts = React.useMemo(
    () =>
      filteredClientContacts?.sort((o1, o2) =>
        (o1.lastName || "").toLowerCase().localeCompare((o2.lastName || "").toLowerCase())
      ) || [],
    [filteredClientContacts]
  );

  const paginatedClientContacts = React.useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedClientContacts.slice(startIndex, endIndex);
  }, [sortedClientContacts, page, rowsPerPage]);

  const cognitoUserSummaryResults = useQueries(
    paginatedClientContacts.map(clientContact => ({
      queryKey: ["listCognitoUsers", clientContact.email],
      queryFn: () =>
        UserAccountsService.listCognitoUsers({
          userPool: CognitoUserPoolType.PORTAL_USER_POOL,
          email: clientContact.email
        })
    }))
  );

  // Build a flat array of all cognito user summaries with their contact index, sorted by username
  const allCognitoUserSummaries = React.useMemo(() => {
    const result: { contactIndex: number; summary: CognitoUserSummary }[] = [];
    cognitoUserSummaryResults.forEach((summaryResult, contactIndex) => {
      const items = summaryResult.data?.items || [];
      const sorted = [...items].sort(sortByUsername);
      sorted.forEach(summary => {
        result.push({ contactIndex, summary });
      });
    });
    return result;
  }, [cognitoUserSummaryResults]);

  // Fetch details for ALL cognito users across all contacts
  const cognitoUserDetailsResults = useQueries(
    allCognitoUserSummaries.map(({ summary }) => ({
      queryKey: ["getCognitoUser", summary.username],
      queryFn: () =>
        UserAccountsService.getCognitoUser({
          userPool: CognitoUserPoolType.PORTAL_USER_POOL,
          cognitoUsername: encodeURIComponent(summary.username)
        }),
      enabled: !!summary.username
    }))
  );

  // Helper: get sorted cognito summaries for a contact
  const getSortedSummariesForContact = React.useCallback(
    (contact: ClientContactSummary) => {
      const contactIndex = paginatedClientContacts.findIndex(c => c.id === contact.id);
      const result = cognitoUserSummaryResults[contactIndex];
      if (!result?.data?.items) {
        return { summaries: [] as CognitoUserSummary[], isLoading: result?.isLoading ?? true };
      }
      return {
        summaries: [...result.data.items].sort(sortByUsername),
        isLoading: false
      };
    },
    [paginatedClientContacts, cognitoUserSummaryResults]
  );

  // Helper: get cognito user details for a contact (ordered by username)
  const getDetailsForContact = React.useCallback(
    (contact: ClientContactSummary) => {
      const contactIndex = paginatedClientContacts.findIndex(c => c.id === contact.id);
      const indices: number[] = [];
      allCognitoUserSummaries.forEach((entry, flatIndex) => {
        if (entry.contactIndex === contactIndex) {
          indices.push(flatIndex);
        }
      });
      return {
        details: indices.map(i => cognitoUserDetailsResults[i]),
        isLoading: indices.some(i => cognitoUserDetailsResults[i]?.isLoading)
      };
    },
    [paginatedClientContacts, allCognitoUserSummaries, cognitoUserDetailsResults]
  );

  const rowKey = React.useCallback((contact: ClientContactSummary) => contact.id.toString(), []);
  const contactFirstNameCell = React.useCallback((contact: ClientContactSummary) => contact.firstName, []);
  const contactLastNameCell = React.useCallback((contact: ClientContactSummary) => contact.lastName, []);
  const contactEmailCell = React.useCallback((contact: ClientContactSummary) => contact.email, []);
  const xtrfIdCell = React.useCallback((contact: ClientContactSummary) => contact.id, []);
  const contactXtrfStatusCell = React.useCallback(
    (contact: ClientContactSummary) => (contact.xtrfAccountStatus ? "Enabled" : "Disabled"),
    []
  );

  const cognitoUsernameCell = React.useCallback(
    (contact: ClientContactSummary) => {
      const { summaries, isLoading: isSummaryLoading } = getSortedSummariesForContact(contact);
      if (isSummaryLoading) {
        return <Skeleton variant="rounded" animation="wave" />;
      }
      if (summaries.length === 0) {
        return "-";
      }
      return (
        <Stack spacing={0}>
          {summaries.map(s => (
            <Typography key={s.username} variant="body2" noWrap>
              {s.username}
            </Typography>
          ))}
        </Stack>
      );
    },
    [getSortedSummariesForContact]
  );

  const contactCognitoStatusCell = React.useCallback(
    (contact: ClientContactSummary) => {
      const { summaries, isLoading: isSummaryLoading } = getSortedSummariesForContact(contact);
      if (isSummaryLoading) {
        return <Skeleton variant="rounded" animation="wave" />;
      }
      if (summaries.length === 0) {
        return "-";
      }
      return (
        <Stack spacing={0}>
          {summaries.map(s => (
            <Typography key={s.username} variant="body2">
              {s.enabled === true ? "Enabled" : s.enabled === false ? "Disabled" : "-"}
            </Typography>
          ))}
        </Stack>
      );
    },
    [getSortedSummariesForContact]
  );

  const cognitoAttributesCell = React.useCallback(
    (contact: ClientContactSummary) => {
      const { summaries, isLoading: isSummaryLoading } = getSortedSummariesForContact(contact);
      if (isSummaryLoading) {
        return <Skeleton variant="rounded" animation="wave" />;
      }
      if (summaries.length === 0) {
        return (
          <Typography variant="body2" align="center">
            -
          </Typography>
        );
      }
      return (
        <Stack spacing={0}>
          {summaries.map(s => {
            if (!s.xtrfClientId && !s.xtrfClientContactId) {
              return (
                <Typography key={s.username} variant="body2" align="center">
                  -
                </Typography>
              );
            }
            return (
              <Stack key={s.username} direction="row" justifyContent="center" spacing={1}>
                <Typography
                  variant="body2"
                  sx={theme =>
                    s.xtrfClientId !== `${contact.clientId}`
                      ? { color: theme.palette.error.main, fontWeight: "bold" }
                      : {}
                  }
                >
                  {s.xtrfClientId}
                </Typography>
                <span>/</span>
                <Typography
                  variant="body2"
                  sx={theme =>
                    s.xtrfClientContactId !== `${contact.id}`
                      ? { color: theme.palette.error.main, fontWeight: "bold" }
                      : {}
                  }
                >
                  {s.xtrfClientContactId}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      );
    },
    [getSortedSummariesForContact]
  );

  const conflictsCell = React.useCallback(
    (contact: ClientContactSummary) => {
      const { details, isLoading: isDetailsLoading } = getDetailsForContact(contact);
      if (isDetailsLoading) {
        return <Skeleton variant="rounded" animation="wave" />;
      }
      if (details.length === 0) {
        return "-";
      }
      return (
        <Stack spacing={0}>
          {details.map(detail => {
            const cognitoUser = detail?.data as CognitoUser | undefined;
            if (!cognitoUser) {
              return "-";
            }
            const hasConflict =
              contact.email !== cognitoUser.email ||
              contact.firstName !== cognitoUser.firstName ||
              contact.lastName !== cognitoUser.lastName ||
              contact.clientId !== cognitoUser.xtrfClientId ||
              `${contact.id}` !== cognitoUser.xtrfClientContactId;
            return hasConflict ? (
              <WarningIcon key={cognitoUser.username} color="error" fontSize="small" />
            ) : (
              <CheckCircleIcon key={cognitoUser.username} color="success" fontSize="small" />
            );
          })}
        </Stack>
      );
    },
    [getDetailsForContact]
  );

  const lastVisitCell = React.useCallback(
    (contact: ClientContactSummary) => {
      const { details, isLoading: isDetailsLoading } = getDetailsForContact(contact);
      if (isDetailsLoading) {
        return <Skeleton variant="rounded" animation="wave" />;
      }
      if (details.length === 0) {
        return "-";
      }
      return (
        <Stack spacing={0}>
          {details.map(detail => {
            const cognitoUser = detail?.data as CognitoUser | undefined;
            return (
              <Typography key={cognitoUser?.username || "empty"} variant="body2">
                {cognitoUser?.lastVisit ? formatEpoch(cognitoUser.lastVisit) : "-"}
              </Typography>
            );
          })}
        </Stack>
      );
    },
    [getDetailsForContact]
  );

  const isOrgAdminCell = React.useCallback(
    (contact: ClientContactSummary) => {
      const { details, isLoading: isDetailsLoading } = getDetailsForContact(contact);
      if (isDetailsLoading) {
        return <Skeleton variant="rounded" animation="wave" />;
      }
      if (details.length === 0) {
        return "-";
      }
      return (
        <Stack spacing={0}>
          {details.map(detail => {
            const cognitoUser = detail?.data as CognitoUser | undefined;
            return (
              <Typography key={cognitoUser?.username || "empty"} variant="body2">
                {cognitoUser?.groups ? (cognitoUser.groups.includes(UserGroup.CLIENT) ? "Yes" : "No") : "-"}
              </Typography>
            );
          })}
        </Stack>
      );
    },
    [getDetailsForContact]
  );

  const onPageChange = React.useCallback((_, selectedPage: number) => {
    setPage(selectedPage);
  }, []);

  const onRowsPerPageChange = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setPage(0);
    setRowsPerPage(+event.target.value);
  }, []);

  const onRowClick = React.useCallback(
    (contact: ClientContactSummary) => {
      setSelectedContact(contact);
      openClientContactDetailsPanel();
    },
    [openClientContactDetailsPanel]
  );

  const rowActions = React.useCallback(
    (contact: ClientContactSummary): DataTableRowAction[] => [
      {
        title: "View Details",
        action: () => {
          setSelectedContact(contact);
          openClientContactDetailsPanel();
        }
      }
    ],
    [openClientContactDetailsPanel]
  );

  const onCloseClientContactDetailsPanel = React.useCallback(() => {
    const contactIndex = paginatedClientContacts.findIndex(u => u.id === selectedContact?.id);
    cognitoUserSummaryResults[contactIndex]?.refetch();
    // Refetch all detail queries for this contact
    allCognitoUserSummaries.forEach((entry, flatIndex) => {
      if (entry.contactIndex === contactIndex) {
        cognitoUserDetailsResults[flatIndex]?.refetch();
      }
    });
    closeClientContactDetailsPanel();
  }, [
    closeClientContactDetailsPanel,
    allCognitoUserSummaries,
    cognitoUserDetailsResults,
    cognitoUserSummaryResults,
    paginatedClientContacts,
    selectedContact?.id
  ]);

  return (
    <>
      {batchDisableError && <SnackbarApiError error={batchDisableError} />}
      {snackbarAlert}
      <Stack direction="row" alignItems="center" justifyContent="space-between" my={2}>
        <Formik
          initialValues={{}}
          onSubmit={() => {
            setPage(0);
            setApplyClientContactsFilter(clientContactsFilter);
          }}
        >
          <Form>
            <Paper sx={{ p: "2px 4px", display: "flex", alignItems: "center", width: 400 }}>
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder="Search client contact ..."
                onChange={event => setClientContactsFilter(event.currentTarget.value)}
              />
              <IconButton type="submit" sx={{ p: "10px" }} aria-label="search">
                <SearchIcon />
              </IconButton>
            </Paper>
          </Form>
        </Formik>
        {isAdminOrSuperUser && !isReadOnly && (
          <LoadingButton
            variant="outlined"
            color="error"
            type="button"
            onClick={openConfirmDialog}
            isLoading={isBatchDisableInProgress}
          >
            Disable Non-SSO Accounts
          </LoadingButton>
        )}
      </Stack>
      {confirmDialogOpen && (
        <ConfirmationDialog
          title="Disable Non-SSO Accounts"
          content="Are you sure you want to disable Cognito accounts for all client contacts that do not have federated SSO accounts? This operation may take some time."
          onClose={onCloseConfirmDialog}
          onConfirm={onConfirmBatchDisable}
          isLoading={isBatchDisableMutationLoading}
          error={batchDisableError ? <ApiErrorMessage apiError={batchDisableError} /> : undefined}
          confirmationButtonColor="error"
        />
      )}
      <DataTable
        columns={[
          {
            id: "firstName",
            title: "First Name",
            cell: contactFirstNameCell
          },
          {
            id: "lastName",
            title: "Last Name",
            cell: contactLastNameCell
          },
          {
            id: "email",
            title: "Email",
            cell: contactEmailCell
          },
          {
            id: "xtrfStatus",
            title: "XTRF Status",
            cell: contactXtrfStatusCell
          },
          {
            id: "xtrfId",
            title: "XTRF ID",
            cell: xtrfIdCell
          },
          {
            id: "cognitoUsername",
            title: "Cognito Username",
            cell: cognitoUsernameCell
          },
          {
            id: "cognitoStatus",
            title: "Cognito Status",
            cell: contactCognitoStatusCell
          },
          {
            id: "cognitoAttrs",
            title: (
              <Stack textAlign="center">
                <div>Cognito Attributes</div>
                <div>(xtrfClientId / xtrfClientContactId)</div>
              </Stack>
            ),
            cell: cognitoAttributesCell
          },
          {
            id: "conflicts",
            title: (
              <Stack direction="row" spacing={1}>
                <div>Conflicts</div>
                <InfoIcon tooltip="Indicates if there are discrepencies between XTRF and Cognito user attributes in such values as the user's email, first name, last name, client ID and client contact ID." />
              </Stack>
            ),
            cell: conflictsCell
          },
          {
            id: "isOrgAdmin",
            title: "Org Admin",
            cell: isOrgAdminCell
          },
          {
            id: "lastVisit",
            title: "Last Visit",
            cell: lastVisitCell
          }
        ]}
        data={paginatedClientContacts}
        isLoading={isLoading}
        error={!!error && <ApiErrorMessage apiError={error} />}
        rowKey={rowKey}
        rowActions={rowActions}
        onRowClick={onRowClick}
        dense={true}
      />

      {filteredClientContacts.length > 0 && (
        <TablePagination
          component="div"
          count={filteredClientContacts.length}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}

      {isClientContactDetailsPanelOpen && selectedContact !== undefined && (
        <ClientContactDetailsPanel clientContact={selectedContact} onClose={onCloseClientContactDetailsPanel} />
      )}
    </>
  );
};

export { ClientContactsTab };
